import { computed, MaybeRef, MaybeRefOrGetter, toValue } from 'vue'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { hcaClientFactory } from '@/api/factories/HcaClientFactory'
import { whenAuthenticatedAnd } from '@/utils/query-utils'
import { ComponentType } from '@/configuration/component/component-types'

const DEFAULT_PAGE_SIZE = 3

export const useCommentsQuery = (
    componentSid: MaybeRefOrGetter<string | undefined>,
    componentType: MaybeRefOrGetter<ComponentType | undefined>,
    preloadSize: MaybeRef<number | undefined>,
) => {
    return useInfiniteQuery({
        queryKey: ['comments', componentSid],
        queryFn: async ({ signal, pageParam }) => {
            const preloadPageSize = toValue(preloadSize)

            const {
                data: { result },
            } = await hcaClientFactory.commentsApi.listComments(
                toValue(componentSid as string),
                toValue(componentType as ComponentType),
                pageParam,
                preloadPageSize ?? DEFAULT_PAGE_SIZE,
                undefined,
                { signal },
            )
            return result
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            if (toValue(preloadSize) !== undefined) {
                return undefined
            }

            if (lastPage?.total === undefined) {
                return 1
            }

            if (lastPage.total <= lastPageParam * DEFAULT_PAGE_SIZE) {
                return undefined
            } else {
                return lastPageParam + 1
            }
        },
        getPreviousPageParam: () => undefined,
        enabled: whenAuthenticatedAnd(computed(() => !!toValue(componentSid))),
    })
}
